import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Checkbox,
  Chip,
  TextField,
  CircularProgress,
  InputAdornment,
  OutlinedInput,
  ListItem,
  ListItemButton,
  ListItemText,
  FormControlLabel,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import PersonAddRoundedIcon from '@mui/icons-material/PersonAddRounded';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded';
import AccountBalanceRoundedIcon from '@mui/icons-material/AccountBalanceRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { searchCongressMembers } from '../../utils/discoverApi';
import { STATE_OPTIONS } from '../../utils/stateLegislationApi';
import { POLICY_NAV_LABEL } from '../../constants/policyLabels';

const CONGRESS_OPTIONS = [
  { number: 119, label: '119th Congress', sub: '2025–2027 (Current)' },
  { number: 118, label: '118th Congress', sub: '2023–2025' },
];

const STATUS_OPTIONS = [
  { code: 'INTRODUCED', label: 'Introduced' },
  { code: 'IN_COMMITTEE', label: 'In Committee' },
  { code: 'REPORTED', label: 'Reported' },
  { code: 'ON_FLOOR', label: 'On Floor' },
  { code: 'PASSED_HOUSE', label: 'Passed House' },
  { code: 'PASSED_SENATE', label: 'Passed Senate' },
  { code: 'TO_PRESIDENT', label: 'To President' },
  { code: 'SIGNED', label: 'Signed' },
  { code: 'BECAME_LAW', label: 'Became Law' },
  { code: 'VETOED', label: 'Vetoed' },
  { code: 'FAILED', label: 'Failed' },
];

const DEBOUNCE = 500;
const PRIMARY = '#047857';

const sectionSx = { py: 2, borderBottom: '1px solid #f1f5f9' };
const headerSx = { display: 'flex', alignItems: 'center', mb: 1.5, gap: 1 };
const titleSx = { fontSize: 15, fontWeight: 700, color: '#0f172a', flex: 1 };
const countSx = { fontSize: 13, color: '#000', flexShrink: 0 };

function ClearLink({ onClick }) {
  return (
    <Typography
      component="button"
      onClick={onClick}
      sx={{
        background: 'none', border: 'none', cursor: 'pointer', p: 0,
        fontSize: 13, fontWeight: 600, color: '#0f172a', textDecoration: 'underline',
        '&:hover': { color: '#334155' },
      }}
    >
      Clear
    </Typography>
  );
}

function ToggleChip({ label, active, onClick }) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      sx={{
        fontWeight: 500, fontSize: 13, cursor: 'pointer', transition: 'all 0.15s',
        bgcolor: active ? PRIMARY : '#fff',
        color: active ? '#fff' : '#0f172a',
        border: active ? `1px solid ${PRIMARY}` : '1px solid #cbd5e1',
        '&:hover': {
          bgcolor: active ? '#065f46' : '#f1f5f9',
          borderColor: active ? '#065f46' : '#94a3b8',
        },
      }}
    />
  );
}

const CHAMBER_OPTIONS = [
  { value: 'House', label: 'House' },
  { value: 'Senate', label: 'Senate' },
];

const DEFAULT_BILL_SORT = 'most_liked';

const SORT_OPTIONS = [
  { id: 'most_liked', label: 'Most Liked' },
  { id: 'most_disliked', label: 'Most Disliked' },
];

export default function BillsFilterModal({
  open, onClose,
  draftSenators = [], draftRepresentatives = [],
  onDraftSenatorsChange, onDraftRepresentativesChange,
  draftCongressNums = [], onDraftCongressNumsChange,
  draftChamber = '', onDraftChamberChange,
  draftStatusCodes = [], onDraftStatusCodesChange,
  draftPolicyAreas = [], onDraftPolicyAreasChange,
  policyAreaOptions = [], policyAreasLoading = false,
  onApply,
  selectedState = 'FED',
  draftSelectedState = 'FED',
  onDraftStateChange,
  userState,
  draftVerifiedVotesOnly = false,
  onDraftVerifiedVotesOnlyChange,
  draftSortBy = DEFAULT_BILL_SORT,
  onDraftSortByChange,
  defaultBillSort = DEFAULT_BILL_SORT,
}) {
  const [memberQ, setMemberQ] = useState('');
  const [memberResults, setMemberResults] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [showStateList, setShowStateList] = useState(false);
  const [stateSearch, setStateSearch] = useState('');
  const searchRef = React.useRef(null);

  const isFedDraft = draftSelectedState === 'FED';

  const allSelected = useMemo(
    () => [...draftSenators, ...draftRepresentatives],
    [draftSenators, draftRepresentatives],
  );

  const runSearch = useCallback(async (q) => {
    const trimmed = (q || '').trim();
    if (!trimmed) { setMemberResults([]); return; }
    setMemberLoading(true);
    try {
      const [s, r] = await Promise.all([
        searchCongressMembers({ position: 'senate', query: trimmed, page: 1, pageSize: 100 }),
        searchCongressMembers({ position: 'house', query: trimmed, page: 1, pageSize: 200 }),
      ]);
      const byId = {};
      (s.results || []).forEach((m) => { if (m.bioguide_id) byId[m.bioguide_id] = { ...m, _pos: 'senate' }; });
      (r.results || []).forEach((m) => { if (m.bioguide_id && !byId[m.bioguide_id]) byId[m.bioguide_id] = { ...m, _pos: 'house' }; });
      setMemberResults(Object.values(byId));
    } catch { setMemberResults([]); }
    finally { setMemberLoading(false); }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    if (searchRef.current) clearTimeout(searchRef.current);
    if (!memberQ.trim()) { setMemberResults([]); return; }
    searchRef.current = setTimeout(() => runSearch(memberQ), DEBOUNCE);
    return () => { if (searchRef.current) clearTimeout(searchRef.current); };
  }, [open, memberQ, runSearch]);

  React.useEffect(() => {
    if (!open) { setShowStateList(false); setStateSearch(''); }
  }, [open]);

  const fmtName = (m) => m.full_name || [m.first_name, m.last_name].filter(Boolean).join(' ').trim() || 'Unknown';
  const getBid = (m) => m.bioguide_id || m.bioguideId;
  const isSel = (bid) => draftSenators.some((s) => s.bioguideId === bid) || draftRepresentatives.some((r) => r.bioguideId === bid);

  const addMember = (m) => {
    const bid = getBid(m);
    if (isSel(bid)) return;
    const d = { bioguideId: bid, name: fmtName(m), sponsored: true, cosponsored: false };
    if (m._pos === 'senate') onDraftSenatorsChange([...draftSenators, d]);
    else onDraftRepresentativesChange([...draftRepresentatives, d]);
  };

  const removeMember = (bid) => {
    onDraftSenatorsChange(draftSenators.filter((s) => s.bioguideId !== bid));
    onDraftRepresentativesChange(draftRepresentatives.filter((r) => r.bioguideId !== bid));
  };

  const toggleField = (bid, field) => {
    const inSen = draftSenators.some((x) => x.bioguideId === bid);
    const list = inSen ? draftSenators : draftRepresentatives;
    const set = inSen ? onDraftSenatorsChange : onDraftRepresentativesChange;
    const m = list.find((x) => x.bioguideId === bid);
    if (!m) return;
    const next = { ...m, [field]: !m[field] };
    if (!next.sponsored && !next.cosponsored) set(list.filter((x) => x.bioguideId !== bid));
    else set(list.map((x) => (x.bioguideId === bid ? next : x)));
  };

  const toggleCongress = (num) => {
    const next = draftCongressNums.includes(num) ? draftCongressNums.filter((n) => n !== num) : [...draftCongressNums, num];
    onDraftCongressNumsChange(next);
  };

  const toggleStatus = (code) => {
    const next = draftStatusCodes.includes(code) ? draftStatusCodes.filter((c) => c !== code) : [...draftStatusCodes, code];
    onDraftStatusCodesChange(next);
  };

  const clearAll = () => {
    onDraftSenatorsChange([]); onDraftRepresentativesChange([]);
    onDraftCongressNumsChange?.([]);
    onDraftChamberChange?.('');
    onDraftStatusCodesChange?.([]);     onDraftPolicyAreasChange?.([]);
    onDraftVerifiedVotesOnlyChange?.(false);
    onDraftSortByChange?.(defaultBillSort);
    onDraftStateChange?.('FED');
    setMemberQ(''); setMemberResults([]);
  };

  const hasFilters =
    allSelected.length > 0 ||
    draftCongressNums.length > 0 || !!draftChamber || draftStatusCodes.length > 0 || draftPolicyAreas.length > 0 ||
    draftSelectedState !== 'FED' || draftVerifiedVotesOnly ||
    (isFedDraft && draftSortBy !== defaultBillSort);

  const draftStateName = STATE_OPTIONS.find(s => s.code === draftSelectedState)?.name || 'Federal';

  // Ordered state list: FED first, then user's state (if set), then rest
  const orderedStates = useMemo(() => {
    const ordered = [STATE_OPTIONS[0]]; // FED
    if (userState && userState !== 'FED') {
      const userOpt = STATE_OPTIONS.find(s => s.code === userState);
      if (userOpt) ordered.push(userOpt);
    }
    STATE_OPTIONS.forEach(s => {
      if (s.code !== 'FED' && s.code !== userState) ordered.push(s);
    });
    return ordered;
  }, [userState]);

  const filteredStates = useMemo(() => {
    if (!stateSearch.trim()) return orderedStates;
    const q = stateSearch.toLowerCase();
    return orderedStates.filter(s => s.name.toLowerCase().includes(q) || s.code.toLowerCase().includes(q));
  }, [orderedStates, stateSearch]);

  // ── State list sub-screen ───────────────────────────────────────────────────
  if (showStateList) {
    return (
      <Dialog
        open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper"
        PaperProps={{ sx: { borderRadius: '16px', bgcolor: '#fff', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, pt: 2, pb: 1.5, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
          <Box
            component="button"
            onClick={() => { setShowStateList(false); setStateSearch(''); }}
            sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, display: 'flex', borderRadius: '50%', '&:hover': { bgcolor: '#f1f5f9' } }}
          >
            <ArrowBackRoundedIcon sx={{ fontSize: 22, color: '#334155' }} />
          </Box>
          <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#0f172a', flex: 1 }}>Select Legislature</Typography>
        </Box>

        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <OutlinedInput
            size="small" fullWidth
            placeholder="Search states…"
            value={stateSearch}
            onChange={(e) => setStateSearch(e.target.value)}
            startAdornment={<InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment>}
            sx={{
              borderRadius: '10px', fontSize: 14, bgcolor: '#fff',
              '& fieldset': { borderColor: '#cbd5e1' },
              '&:hover fieldset': { borderColor: '#94a3b8' },
              '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1 },
              '& input::placeholder': { color: '#94a3b8', opacity: 1 },
            }}
          />
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
          {filteredStates.map(st => {
            const isActive = draftSelectedState === st.code;
            const isUserState = st.code === userState && st.code !== 'FED';
            return (
              <Box
                key={st.code}
                component="button"
                onClick={() => { onDraftStateChange?.(st.code); setShowStateList(false); setStateSearch(''); }}
                sx={{
                  display: 'flex', alignItems: 'center', width: '100%', gap: 1.5,
                  px: 2, py: 1.5, background: isActive ? '#f0fdf4' : 'none',
                  border: 'none', borderBottom: '1px solid #f1f5f9', cursor: 'pointer',
                  '&:hover': { bgcolor: '#f8fafc' },
                }}
              >
                <Typography sx={{ flex: 1, fontSize: 14, fontWeight: isActive ? 700 : 500, color: isActive ? PRIMARY : '#0f172a', textAlign: 'left' }}>
                  {st.name}
                  {isUserState && <Typography component="span" sx={{ fontSize: 12, color: '#64748b', ml: 1 }}>(Your state)</Typography>}
                </Typography>
                {isActive && <CheckRoundedIcon sx={{ fontSize: 20, color: PRIMARY }} />}
              </Box>
            );
          })}
        </Box>
      </Dialog>
    );
  }

  // ── Main filter screen ──────────────────────────────────────────────────────
  return (
    <Dialog
      open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper"
      PaperProps={{ sx: { borderRadius: '16px', bgcolor: '#fff', maxHeight: '90vh', display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, pt: 2.5, pb: 1.5, borderBottom: '1px solid #e2e8f0', flexShrink: 0 }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>Filter {POLICY_NAV_LABEL.toLowerCase()}</Typography>
        <Box component="button" onClick={onClose} sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.5, display: 'flex', borderRadius: '50%', '&:hover': { bgcolor: '#f1f5f9' } }}>
          <CloseRoundedIcon sx={{ fontSize: 22, color: '#334155' }} />
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 3, pb: 1 }}>

        {isFedDraft && (
          <Box sx={sectionSx}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={draftVerifiedVotesOnly}
                  onChange={(e) => onDraftVerifiedVotesOnlyChange?.(e.target.checked)}
                  icon={<CheckBoxOutlineBlankIcon sx={{ color: '#64748b' }} />}
                  checkedIcon={<CheckBoxIcon sx={{ color: PRIMARY }} />}
                  sx={{ p: 0.5 }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <VerifiedUserRoundedIcon sx={{ fontSize: 18, color: draftVerifiedVotesOnly ? PRIMARY : '#64748b' }} />
                  <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#0f172a' }}>
                    Verified votes only
                  </Typography>
                </Box>
              }
              sx={{ mx: 0, alignItems: 'center' }}
            />
          </Box>
        )}

        {/* Sort — federal only */}
        {isFedDraft && (
          <Box sx={sectionSx}>
            <Box sx={headerSx}>
              <Typography sx={titleSx}>Sort</Typography>
              {draftSortBy !== defaultBillSort && (
                <ClearLink onClick={() => onDraftSortByChange?.(defaultBillSort)} />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {SORT_OPTIONS.map((o) => (
                <ToggleChip
                  key={o.id}
                  label={o.label}
                  active={draftSortBy === o.id}
                  onClick={() => onDraftSortByChange?.(o.id)}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Legislature */}
        <Box sx={sectionSx}>
          <Box
            component="button"
            onClick={() => setShowStateList(true)}
            sx={{
              display: 'flex', alignItems: 'center', width: '100%', gap: 1.5,
              background: 'none', border: 'none', cursor: 'pointer', p: 0,
              '&:hover': { opacity: 0.85 },
            }}
          >
            <AccountBalanceRoundedIcon sx={{ fontSize: 20, color: PRIMARY }} />
            <Box sx={{ flex: 1, textAlign: 'left' }}>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>Legislature</Typography>
              <Typography sx={{ fontSize: 13, color: '#000' }}>{draftStateName}</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: '50%', bgcolor: PRIMARY }}>
              <ChevronRightRoundedIcon sx={{ fontSize: 18, color: '#fff' }} />
            </Box>
          </Box>
        </Box>

        {/* Federal-only filters info */}
        {!isFedDraft && (
          <Box sx={{ py: 1.5, px: 1, mb: 1, bgcolor: '#fffbeb', borderRadius: '8px', border: '1px solid #fef3c7' }}>
            <Typography sx={{ mt: 1, fontSize: 16, color: '#000', fontWeight: 500 }}>
              Federal filters (Congress, Representatives, Status, Policy Area) apply only to federal bills.
            </Typography>
          </Box>
        )}

        {/* Congress — federal only */}
        {isFedDraft && (
          <Box sx={sectionSx}>
            <Box sx={headerSx}>
              <Typography sx={titleSx}>Congress</Typography>
              {draftCongressNums.length > 0 && <ClearLink onClick={() => onDraftCongressNumsChange?.([])} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {CONGRESS_OPTIONS.map((o) => (
                <ToggleChip key={o.number} label={`${o.label} (${o.sub})`} active={draftCongressNums.includes(o.number)} onClick={() => toggleCongress(o.number)} />
              ))}
            </Box>
          </Box>
        )}

        {/* Chamber — federal only */}
        {isFedDraft && (
          <Box sx={sectionSx}>
            <Box sx={headerSx}>
              <Typography sx={titleSx}>Chamber</Typography>
              {draftChamber && <ClearLink onClick={() => onDraftChamberChange?.('')} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {CHAMBER_OPTIONS.map((o) => (
                <ToggleChip
                  key={o.value}
                  label={o.label}
                  active={draftChamber === o.value}
                  onClick={() => onDraftChamberChange?.(draftChamber === o.value ? '' : o.value)}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Representatives & Senators — federal only */}
        {isFedDraft && (
          <Box sx={sectionSx}>
            <Box sx={headerSx}>
              <Typography sx={titleSx}>Representatives & Senators</Typography>
              <Typography sx={countSx}>{allSelected.length === 0 ? '' : `${allSelected.length} selected`}</Typography>
              {allSelected.length > 0 && <ClearLink onClick={() => { onDraftSenatorsChange([]); onDraftRepresentativesChange([]); setMemberQ(''); setMemberResults([]); }} />}
            </Box>
            <OutlinedInput
              size="small" fullWidth
              placeholder="Search senators or representatives…"
              value={memberQ} onChange={(e) => setMemberQ(e.target.value)}
              startAdornment={<InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment>}
              endAdornment={memberLoading ? <InputAdornment position="end"><CircularProgress size={16} sx={{ color: PRIMARY }} /></InputAdornment> : null}
              sx={{
                borderRadius: '10px', fontSize: 14, bgcolor: '#fff', mb: 1,
                '& fieldset': { borderColor: '#cbd5e1' },
                '&:hover fieldset': { borderColor: '#94a3b8' },
                '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1 },
                '& input::placeholder': { color: '#94a3b8', opacity: 1 },
              }}
            />
            {memberResults.length > 0 && (
              <Box sx={{ maxHeight: 160, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', mb: 1 }}>
                {memberResults.map((m) => {
                  const bid = getBid(m);
                  const sel = isSel(bid);
                  return (
                    <ListItem key={bid} disablePadding dense>
                      <ListItemButton onClick={() => addMember(m)} disabled={sel} sx={{ py: 0.75, px: 1.5 }}>
                        <PersonAddRoundedIcon sx={{ fontSize: 16, mr: 1, color: sel ? '#cbd5e1' : PRIMARY }} />
                        <ListItemText
                          primary={fmtName(m)}
                          secondary={m.recent_state ? `(${m.recent_state})` : null}
                          primaryTypographyProps={{ fontSize: 13, fontWeight: 500, color: sel ? '#94a3b8' : '#0f172a' }}
                          secondaryTypographyProps={{ fontSize: 11, color: '#64748b' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </Box>
            )}
            {allSelected.length > 0 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {allSelected.map((m) => (
                  <Box key={m.bioguideId} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75, borderRadius: '8px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#0f172a', flex: 1, minWidth: 0 }}>{m.name}</Typography>
                    <FormControlLabel
                      control={<Checkbox size="small" checked={m.sponsored} onChange={() => toggleField(m.bioguideId, 'sponsored')} sx={{ p: 0.25, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />}
                      label="Sponsor" sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 11, color: '#475569' } }}
                    />
                    <FormControlLabel
                      control={<Checkbox size="small" checked={m.cosponsored} onChange={() => toggleField(m.bioguideId, 'cosponsored')} sx={{ p: 0.25, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }} />}
                      label="Cosponsor" sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 11, color: '#475569' } }}
                    />
                    <Typography
                      component="button" onClick={() => removeMember(m.bioguideId)}
                      sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0, fontSize: 11, fontWeight: 600, color: '#ef4444', '&:hover': { color: '#dc2626' } }}
                    >
                      Remove
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            {memberQ.trim() && !memberLoading && memberResults.length === 0 && (
              <Typography sx={{ fontSize: 13, color: '#64748b', pt: 0.5 }}>
                No members found matching "{memberQ.trim()}".
              </Typography>
            )}
            {allSelected.length === 0 && !memberQ && (
              <Typography sx={{ fontSize: 13, color: '#000', pt: 0.5 }}>
                Search to add any senator or representative.
              </Typography>
            )}
          </Box>
        )}

        {/* Status — federal only */}
        {isFedDraft && (
          <Box sx={sectionSx}>
            <Box sx={headerSx}>
              <Typography sx={titleSx}>Status</Typography>
              <Typography sx={countSx}>{draftStatusCodes.length === 0 ? '' : `${draftStatusCodes.length} selected`}</Typography>
              {draftStatusCodes.length > 0 && <ClearLink onClick={() => onDraftStatusCodesChange?.([])} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {STATUS_OPTIONS.map((o) => (
                <ToggleChip key={o.code} label={o.label} active={draftStatusCodes.includes(o.code)} onClick={() => toggleStatus(o.code)} />
              ))}
            </Box>
          </Box>
        )}

        {/* Policy Area — federal only */}
        {isFedDraft && (
          <Box sx={{ pt: 2, pb: 1 }}>
            <Box sx={headerSx}>
              <Typography sx={titleSx}>Policy Area</Typography>
              <Typography sx={countSx}>{draftPolicyAreas.length === 0 ? '' : `${draftPolicyAreas.length} selected`}</Typography>
              {draftPolicyAreas.length > 0 && <ClearLink onClick={() => onDraftPolicyAreasChange?.([])} />}
            </Box>
            {policyAreasLoading ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                <CircularProgress size={20} sx={{ color: PRIMARY }} />
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Loading…</Typography>
              </Box>
            ) : policyAreaOptions.length === 0 ? (
              <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>Policy areas will appear as more bills are synced.</Typography>
            ) : (
              <Autocomplete
                multiple disableCloseOnSelect
                options={policyAreaOptions}
                value={draftPolicyAreas}
                onChange={(_, v) => onDraftPolicyAreasChange(v)}
                renderOption={(props, option, { selected }) => {
                  const { key, ...rest } = props;
                  return (
                    <li key={key} {...rest} style={{ padding: '6px 12px' }}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon sx={{ fontSize: 18 }} />}
                        checkedIcon={<CheckBoxIcon sx={{ fontSize: 18 }} />}
                        checked={selected} size="small"
                        sx={{ p: 0, mr: 1, color: PRIMARY, '&.Mui-checked': { color: PRIMARY } }}
                      />
                      <span style={{ fontSize: 13, color: '#0f172a' }}>{option}</span>
                    </li>
                  );
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, i) => {
                    const { key, ...rest } = getTagProps({ index: i });
                    return (
                      <Chip key={key} label={option} size="small" {...rest}
                        sx={{ bgcolor: '#f0fdf4', color: '#0f172a', fontWeight: 500, fontSize: 12, border: '1px solid #bbf7d0', '& .MuiChip-deleteIcon': { fontSize: 16, color: '#64748b', '&:hover': { color: '#0f172a' } } }}
                      />
                    );
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} placeholder={draftPolicyAreas.length === 0 ? 'Search policy areas…' : ''} size="small"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: 13, '& fieldset': { borderColor: '#cbd5e1' }, '&:hover fieldset': { borderColor: '#94a3b8' }, '&.Mui-focused fieldset': { borderColor: PRIMARY, borderWidth: 1 }, '& input': { color: '#0f172a' }, '& input::placeholder': { color: '#94a3b8', opacity: 1 } } }}
                  />
                )}
                slotProps={{ popper: { sx: { zIndex: 1400 } }, popupIndicator: { sx: { color: '#475569' } }, clearIndicator: { sx: { color: '#475569' } } }}
                ListboxProps={{ sx: { maxHeight: 220, '& .MuiAutocomplete-option': { minHeight: 36 } } }}
              />
            )}
          </Box>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ display: 'flex', gap: 1.5, px: 3, py: 2, borderTop: '1px solid #e2e8f0', flexShrink: 0, bgcolor: '#fff' }}>
        <Box
          component="button" onClick={clearAll} disabled={!hasFilters}
          sx={{
            flex: 1, py: 1.25, borderRadius: '10px', fontSize: 14, fontWeight: 600, cursor: hasFilters ? 'pointer' : 'default',
            bgcolor: '#fff', color: hasFilters ? '#0f172a' : '#cbd5e1', border: hasFilters ? '1px solid #0f172a' : '1px solid #e2e8f0',
            transition: 'all 0.15s', '&:hover:not(:disabled)': { bgcolor: '#f8fafc' },
          }}
        >
          Clear all
        </Box>
        <Box
          component="button" onClick={() => { onApply(); onClose(); }}
          sx={{
            flex: 1, py: 1.25, borderRadius: '10px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
            bgcolor: PRIMARY, color: '#fff', border: `1px solid ${PRIMARY}`,
            transition: 'all 0.15s', '&:hover': { bgcolor: '#065f46' },
          }}
        >
          Apply filters
        </Box>
      </Box>
    </Dialog>
  );
}
